import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription } from
'@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Card, CardContent } from '@/shared/components/ui/card';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger } from
'@/shared/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
'@/shared/components/ui/select';
import {
  Printer,
  Eye,
  Code,
  Copy,
  Download,
  CheckCircle2,
  Loader2,
  Package,
  Tag,
  Truck,
  Barcode } from
'lucide-react';
import { toast } from 'sonner';
import { devicesAPI } from '@/shared/services/api';

// Parse ZPL to extract label content for visual preview
const parseZPLContent = (zpl) => {
  const content = {
    texts: [],
    barcodes: [],
    lines: [],
    width: 812, // Default 4" at 203dpi
    height: 1218 // Default 6" at 203dpi
  };

  if (!zpl) return content;

  // Extract label dimensions
  const pwMatch = zpl.match(/\^PW(\d+)/);
  const llMatch = zpl.match(/\^LL(\d+)/);
  if (pwMatch) content.width = parseInt(pwMatch[1]);
  if (llMatch) content.height = parseInt(llMatch[1]);

  // Extract text fields (^FD...^FS)
  const textRegex = /\^FO(\d+),(\d+).*?\^FD(.+?)\^FS/g;
  let match;
  while ((match = textRegex.exec(zpl)) !== null) {
    content.texts.push({
      x: parseInt(match[1]),
      y: parseInt(match[2]),
      text: match[3]
    });
  }

  // Extract barcodes (^BC)
  const barcodeRegex = /\^FO(\d+),(\d+).*?\^BC.*?\^FD(.+?)\^FS/g;
  while ((match = barcodeRegex.exec(zpl)) !== null) {
    content.barcodes.push({
      x: parseInt(match[1]),
      y: parseInt(match[2]),
      data: match[3]
    });
  }

  // Extract lines/boxes (^GB)
  const lineRegex = /\^FO(\d+),(\d+)\^GB(\d+),(\d+),(\d+)/g;
  while ((match = lineRegex.exec(zpl)) !== null) {
    content.lines.push({
      x: parseInt(match[1]),
      y: parseInt(match[2]),
      width: parseInt(match[3]),
      height: parseInt(match[4]),
      thickness: parseInt(match[5])
    });
  }

  return content;
};

// Visual label preview component
const LabelVisualPreview = ({ zplCode, labelType }) => {const { t } = useTranslation("wms");
  const content = parseZPLContent(zplCode);
  const scale = 0.4; // Scale down for display

  return (
    <div className="flex justify-center p-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
      <div
        className="bg-white border-2 border-gray-300 shadow-lg relative"
        style={{
          width: content.width * scale,
          height: content.height * scale,
          minWidth: '300px',
          minHeight: '400px'
        }}>
        
        {/* Render lines/boxes */}
        {content.lines.map((line, i) =>
        <div
          key={`line-${i}`}
          className="absolute bg-black"
          style={{
            left: line.x * scale,
            top: line.y * scale,
            width: line.width * scale,
            height: Math.max(line.height * scale, line.thickness * scale)
          }} />

        )}

        {/* Render text fields */}
        {content.texts.map((text, i) =>
        <div
          key={`text-${i}`}
          className="absolute text-black font-mono whitespace-nowrap"
          style={{
            left: text.x * scale,
            top: text.y * scale,
            fontSize: `${10 * scale}px`,
            transform: 'scale(1.5)',
            transformOrigin: 'top left'
          }}>
          
            {text.text}
          </div>
        )}

        {/* Render barcodes (visual representation) */}
        {content.barcodes.map((barcode, i) =>
        <div
          key={`barcode-${i}`}
          className="absolute"
          style={{
            left: barcode.x * scale,
            top: barcode.y * scale
          }}>
          
            <div className="flex gap-px">
              {/* Simple barcode visualization */}
              {Array.from({ length: 40 }).map((_, j) =>
            <div
              key={j}
              className="bg-black"
              style={{
                width: Math.random() > 0.5 ? '2px' : '1px',
                height: `${40 * scale}px`
              }} />

            )}
            </div>
            <p className="text-xs font-mono text-center mt-1" style={{ fontSize: `${8 * scale}px` }}>
              {barcode.data}
            </p>
          </div>
        )}

        {/* Label type indicator */}
        <div className="absolute bottom-2 right-2">
          <Badge variant="outline" className="text-xs bg-white">
            {labelType || 'Label'}
          </Badge>
        </div>
      </div>
    </div>);

};

export const LabelPreviewModal = ({
  open,
  onOpenChange,
  labelData,
  onPrint,
  cartonId
}) => {const { t } = useTranslation("wms");
  const [activeTab, setActiveTab] = useState('preview');
  const [printers, setPrinters] = useState([]);
  const [selectedPrinter, setSelectedPrinter] = useState('');
  const [copies, setCopies] = useState(1);
  const [loading, setLoading] = useState(false);
  const [printing, setPrinting] = useState(false);

  // Fetch available printers
  useEffect(() => {
    if (open) {
      fetchPrinters();
    }
  }, [open]);

  const fetchPrinters = async () => {
    try {
      setLoading(true);
      const data = await devicesAPI.getAvailablePrinters();
      setPrinters(data || []);
      if (data?.length > 0) {
        setSelectedPrinter(data[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch printers:', error);
      toast.error('Failed to load printers');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyZPL = () => {
    if (labelData?.zpl_code) {
      navigator.clipboard.writeText(labelData.zpl_code);
      toast.success('ZPL Code Copied', { description: 'Label code copied to clipboard' });
    }
  };

  const handleDownloadZPL = () => {
    if (labelData?.zpl_code) {
      const blob = new Blob([labelData.zpl_code], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `label_${cartonId || 'export'}.zpl`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('ZPL Downloaded');
    }
  };

  const handlePrint = async () => {
    if (!selectedPrinter) {
      toast.error('Please select a printer');
      return;
    }

    setPrinting(true);
    try {
      // Call print API
      await devicesAPI.submitPrintJob(
        selectedPrinter,
        labelData?.label_type || 'shipping',
        labelData?.content || {},
        copies
      );
      toast.success('Print Job Submitted', {
        description: `Printing ${copies} label(s) to ${printers.find((p) => p.id === selectedPrinter)?.name}`
      });
      onPrint?.({ printer: selectedPrinter, copies });
      onOpenChange(false);
    } catch (error) {
      console.error('Print failed:', error);
      toast.error('Print Failed', { description: error.message });
    } finally {
      setPrinting(false);
    }
  };

  const getLabelIcon = () => {
    switch (labelData?.label_type) {
      case 'shipping':return Truck;
      case 'carton':return Package;
      case 'product':return Barcode;
      default:return Tag;
    }
  };

  const LabelIcon = getLabelIcon();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LabelIcon className="h-5 w-5" />{t("labelPreview", "Label Preview")}

            {labelData?.label_type &&
            <Badge variant="secondary" className="ml-2 capitalize">
                {labelData.label_type}
              </Badge>
            }
          </DialogTitle>
          <DialogDescription>{t("previewLabelContent", "Preview label content and print to a configured printer")}

          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="preview" className="gap-2">
              <Eye className="h-4 w-4" />{t("visualPreview", "Visual Preview")}

            </TabsTrigger>
            <TabsTrigger value="code" className="gap-2">
              <Code className="h-4 w-4" />{t("zplCode", "ZPL Code")}

            </TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-auto mt-4">
            <TabsContent value="preview" className="mt-0 h-full">
              {labelData?.zpl_code ?
              <LabelVisualPreview
                zplCode={labelData.zpl_code}
                labelType={labelData.label_type} /> :


              <div className="flex items-center justify-center h-64 text-muted-foreground">
                  <p>{t("noLabelData", "No label data available")}</p>
                </div>
              }
            </TabsContent>

            <TabsContent value="code" className="mt-0">
              <div className="space-y-3">
                <div className="flex justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={handleCopyZPL} className="gap-2">
                    <Copy className="h-4 w-4" />{t("copy", "Copy")}

                  </Button>
                  <Button variant="outline" size="sm" onClick={handleDownloadZPL} className="gap-2">
                    <Download className="h-4 w-4" />{t("downloadZpl", "Download .zpl")}

                  </Button>
                </div>
                <Card className="bg-gray-900 text-gray-100">
                  <CardContent className="p-4">
                    <pre className="text-xs font-mono whitespace-pre-wrap overflow-auto max-h-80">
                      {labelData?.zpl_code || 'No ZPL code available'}
                    </pre>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </div>
        </Tabs>

        {/* Print Options */}
        <div className="border-t pt-4 mt-4">
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("printer", "Printer")}</label>
              <Select value={selectedPrinter} onValueChange={setSelectedPrinter}>
                <SelectTrigger data-testid="printer-select">
                  <SelectValue placeholder={loading ? "Loading..." : "Select printer"} />
                </SelectTrigger>
                <SelectContent>
                  {printers.map((printer) =>
                  <SelectItem key={printer.id} value={printer.id}>
                      <div className="flex items-center gap-2">
                        <Printer className="h-4 w-4" />
                        {printer.name}
                        {printer.status === 'online' &&
                      <span className="h-2 w-2 rounded-full bg-green-500" />
                      }
                      </div>
                    </SelectItem>
                  )}
                  {printers.length === 0 && !loading &&
                  <SelectItem value="none" disabled>{t("noPrintersAvailable", "No printers available")}

                  </SelectItem>
                  }
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("copies", "Copies")}</label>
              <Select value={String(copies)} onValueChange={(v) => setCopies(parseInt(v))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5].map((n) =>
                  <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("cancel", "Cancel")}

          </Button>
          <Button
            onClick={handlePrint}
            disabled={!selectedPrinter || printing || printers.length === 0}
            className="gap-2"
            data-testid="print-label-btn">
            
            {printing ?
            <>
                <Loader2 className="h-4 w-4 animate-spin" />{t("printing", "Printing...")}

            </> :

            <>
                <Printer className="h-4 w-4" />{t("printLabel", "Print Label")}

            </>
            }
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>);

};

export default LabelPreviewModal;